import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, CheckCircle, Layers, Loader, Package, Target } from 'lucide-react';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { consolidationService } from '../../../../../services/consolidationService';
import { ConsolidationAnalysisResponse, ConsolidationSuggestion } from '../../../../../types/consolidation';
import { ConfirmLine } from '../types';
import { parsedQty } from '../utils';
import { useSourceSelections } from '../useSourceSelections';
import { OpenCapacityArgs } from '../useCapacityUpdate';
import { CapacityConfidenceBadge, FillMeter } from './CapacityIndicators';
import { SourceList } from './SourceList';

interface GatherItemViewProps {
    itemNo: string;
    description1?: string;
    warehouseID: string;
    /** Bumpes av ConsolidationApp etter utført flytt → re-laster forslagene. */
    refreshToken: number;
    onConfirm: (lines: ConfirmLine[]) => void;
    /** Åpne «Oppdater kapasitet» for (mål-lokasjon, vare). */
    onUpdateCapacity?: (args: OpenCapacityArgs) => void;
}

/**
 * Visning B — «Samle vare» (vare-sentrisk).
 * Viser hvor varen ligger spredt og foreslår mål-lokasjon(er) som samler alt
 * og frigjør flest mulig lokasjoner.
 */
export const GatherItemView: React.FC<GatherItemViewProps> = ({
    itemNo,
    description1,
    warehouseID,
    refreshToken,
    onConfirm,
    onUpdateCapacity
}) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [analysis, setAnalysis] = useState<ConsolidationAnalysisResponse | null>(null);

    const suggestions = useMemo<ConsolidationSuggestion[]>(
        () => analysis?.suggestions ?? [],
        [analysis]
    );
    const { selectionFor, toggle, setQty, selectedCount } = useSourceSelections(suggestions);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await consolidationService.analyze({
                    warehouseID,
                    mode: 'item',
                    itemNo,
                    includeEstimatedCapacity: true,
                    // Vis også mål-bins der Compass tror det ikke er plass — operatøren kan
                    // oppdatere kapasiteten i stedet for å få «ingen samleforslag».
                    includeCapacityLimited: true,
                    topN: 5
                });
                if (cancelled) return;
                if (!response.success) {
                    setError(response.errorMessage || t('warehouse.modules.consolidation.errors.loadFailed', {}, 'Kunne ikke laste data'));
                    setAnalysis(null);
                } else {
                    setAnalysis(response);
                }
            } catch (err) {
                if (cancelled) return;
                setError(err instanceof Error ? err.message : t('warehouse.modules.consolidation.errors.loadFailed', {}, 'Kunne ikke laste data'));
                setAnalysis(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemNo, warehouseID, refreshToken]);

    const itemDescription = description1 || suggestions[0]?.description1;
    const totalBinsFreed = analysis?.summary.binsFreed ?? 0;

    const handleConfirm = () => {
        const lines: ConfirmLine[] = [];
        suggestions.forEach((suggestion, index) => {
            suggestion.sources.forEach((source) => {
                const selection = selectionFor(index, source.fromBinCode);
                if (!selection.checked || parsedQty(selection.qty) <= 0) return;
                lines.push({
                    id: `${suggestion.itemNo}|${source.fromBinCode}|${suggestion.target.binCode}`,
                    fromBinCode: source.fromBinCode,
                    toBinCode: suggestion.target.binCode,
                    originalToBinCode: suggestion.target.binCode,
                    itemNo: suggestion.itemNo,
                    description1: suggestion.description1 || description1,
                    quantity: selection.qty,
                    maxQuantity: source.quantity,
                    freesBin: source.freesBin,
                    capacityConfidence: suggestion.target.capacityConfidence,
                    status: 'pending'
                });
            });
        });
        if (lines.length > 0) onConfirm(lines);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12 bg-white rounded-lg border shadow-sm">
                <Loader className="animate-spin h-6 w-6 text-cyan-500" />
                <span className="ml-2 text-sm text-gray-600">
                    {t('warehouse.modules.consolidation.gatherItem.loading', {}, 'Analyserer spredning…')}
                </span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start text-sm text-red-800">
                <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                <span className="break-words">{error}</span>
            </div>
        );
    }

    return (
        <div className="space-y-3">
            {/* Vare-header */}
            <div className="bg-white rounded-lg p-4 shadow-sm border">
                <div className="flex items-center gap-2">
                    <Package className="h-5 w-5 text-cyan-500 flex-shrink-0" />
                    <span className="font-mono text-lg font-semibold text-gray-900 break-all">{itemNo}</span>
                </div>
                {itemDescription && (
                    <div className="text-sm text-gray-500 mt-1 break-words">{itemDescription}</div>
                )}
            </div>

            {suggestions.length === 0 ? (
                /* Vennlig tom-tilstand */
                <div className="bg-white rounded-lg p-6 shadow-sm border text-center">
                    <CheckCircle className="h-10 w-10 text-green-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">
                        {t('warehouse.modules.consolidation.gatherItem.empty', {}, 'Ingen samleforslag for denne varen — alt ligger allerede samlet, eller ingen mål-lokasjon har plass.')}
                    </p>
                </div>
            ) : (
                <>
                    {/* Gevinst-banner */}
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center text-sm text-green-800">
                        <CheckCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                        <span className="break-words">
                            {t(
                                'warehouse.modules.consolidation.gatherItem.gainBanner',
                                { '0': suggestions[0].target.binCode, '1': totalBinsFreed },
                                `Samler alt i ${suggestions[0].target.binCode} → frigjør ${totalBinsFreed} lokasjoner`
                            )}
                        </span>
                    </div>

                    {suggestions.map((suggestion, index) => (
                        <div key={`${suggestion.target.binCode}-${index}`} className="space-y-3">
                            {/* Mål-bin-kort (uthevet) */}
                            <div className="bg-white rounded-lg p-4 shadow-sm border-2 border-blue-500">
                                <div className="flex items-center justify-between gap-2 mb-1">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <Target className="h-5 w-5 text-blue-600 flex-shrink-0" />
                                        <span className="font-mono text-lg font-semibold text-gray-900 break-all">
                                            {suggestion.target.binCode}
                                        </span>
                                    </div>
                                    <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full flex-shrink-0">
                                        {t('warehouse.modules.consolidation.gatherItem.targetBadge', {}, 'MÅL')}
                                    </span>
                                </div>
                                <div className="text-sm text-gray-600">
                                    {t('warehouse.modules.consolidation.gatherItem.balanceNow', { '0': suggestion.target.currentQuantity }, `Saldo nå: ${suggestion.target.currentQuantity}`)}
                                    {suggestion.target.capacityConfidence !== 'unknown' && (
                                        <span className="ml-3">
                                            {t('warehouse.modules.consolidation.gatherItem.spaceFor', { '0': suggestion.target.maxQuantity }, `Plass til ${suggestion.target.maxQuantity}`)}
                                        </span>
                                    )}
                                </div>
                                <div className="flex items-center gap-2 mt-2">
                                    {suggestion.target.capacityConfidence === 'unknown' ? (
                                        <CapacityConfidenceBadge
                                            confidence="unknown"
                                            approxText={t('warehouse.modules.consolidation.pullIn.capacityApprox', {}, 'ca.')}
                                            unknownText={t('warehouse.modules.consolidation.pullIn.capacityUnknown', {}, 'ukjent — bekreft manuelt')}
                                        />
                                    ) : (
                                        <>
                                            <FillMeter pct={suggestion.target.fillPctAfter} />
                                            {suggestion.target.capacityConfidence === 'estimated' && (
                                                <CapacityConfidenceBadge
                                                    confidence="estimated"
                                                    approxText={t('warehouse.modules.consolidation.pullIn.capacityApprox', {}, 'ca.')}
                                                    unknownText={t('warehouse.modules.consolidation.pullIn.capacityUnknown', {}, 'ukjent — bekreft manuelt')}
                                                />
                                            )}
                                        </>
                                    )}
                                </div>
                                {suggestion.warnings.length > 0 && (
                                    <div className="text-xs text-orange-600 mt-2 break-words">
                                        {suggestion.warnings.join(' · ')}
                                    </div>
                                )}
                                {onUpdateCapacity
                                    && (suggestion.target.capacityConfidence !== 'observed'
                                        || suggestion.sources.some((s) => s.capacityLimited)) && (
                                    <button
                                        onClick={() => onUpdateCapacity({
                                            binCode: suggestion.target.binCode,
                                            itemNo: suggestion.itemNo,
                                            itemDescription: suggestion.description1 || description1,
                                            currentQuantity: suggestion.target.currentQuantity
                                        })}
                                        className="mt-2 flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-cyan-700 border border-cyan-200 hover:bg-cyan-50 min-h-[40px]"
                                        title={t('warehouse.modules.consolidation.capacity.updateHint', {}, 'Det er plass? Oppdater kapasiteten')}
                                    >
                                        <Layers className="h-4 w-4" />
                                        {t('warehouse.modules.consolidation.capacity.update', {}, 'Oppdater kapasitet')}
                                    </button>
                                )}
                            </div>

                            {/* Kilder */}
                            <div className="bg-white rounded-lg p-4 shadow-sm border">
                                <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                                    {t('warehouse.modules.consolidation.gatherItem.sources', {}, 'Hentes fra')}
                                </div>
                                <SourceList
                                    sources={suggestion.sources}
                                    selectionFor={(fromBinCode) => selectionFor(index, fromBinCode)}
                                    onToggle={(fromBinCode) => toggle(index, fromBinCode)}
                                    onQtyChange={(fromBinCode, qty) => setQty(index, fromBinCode, qty)}
                                />
                            </div>
                        </div>
                    ))}

                    <button
                        onClick={handleConfirm}
                        disabled={selectedCount === 0}
                        className={`w-full py-4 rounded-lg font-semibold text-base min-h-[44px] transition-colors ${
                            selectedCount > 0
                                ? 'bg-green-500 text-white hover:bg-green-600'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                    >
                        {t('warehouse.modules.consolidation.gatherItem.gather', { '0': selectedCount }, `Opprett utleggsordre (${selectedCount} flytt)`)}
                    </button>
                </>
            )}
        </div>
    );
};
